import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { connect } from 'react-redux';

import {
  load as loadOrg,
  listAssessmentHistorySummaries,
} from 'js/actions/org-actions';

import { selectAssessmentHistorySummaries } from 'js/store/reducers/queries';

import OrgComponentRouter from '../components/OrgComponent/OrgComponentRouter';
import { denormalize } from 'normalizr';
import { SCHEMAS } from 'js/api/schemas';
import assessmentTemplates from 'js/data/assessment-templates';
import { listSubscriptions } from 'js/actions/assessment-template-actions';
import { useQuery } from 'js/hooks/useQuery';

function transformTemplate(template) {
  return {
    ...template,
    key: template.key ?? template.assessment.key,
    assessment: {
      ...template.assessment,
      document: template.assessment,
      question_sets: template.assessment.pages
        .filter((p) => p.pageType === 'question-set')
        .map((qs) => ({
          ...qs,
          questions: qs.elements
            .filter((e) => e.elementType === 'question')
            .map((q) => ({
              ...q,
              answers: q.answers?.map((a) => {
                const newAnswer = { ...a, guid: a.id };
                delete newAnswer.id;
                return newAnswer;
              }),
            })),
        })),
    },
  };
}

const OrgContainer = ({
  loadOrg,
  listAssessmentHistorySummaries,
  match,
  org,
  workspace,
  assessmentHistorySummaries,
  listSubscriptions,
}) => {
  const {
    params: { orgId, workspaceId },
  } = match;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refreshSubscriptionsQuery = useCallback(
    (queryId) => listSubscriptions(queryId),
    [listSubscriptions]
  );

  const {
    refresh: refreshSubscriptions,
    result: subscriptions,
    hasLoaded: subscriptionsHaveLoaded,
  } = useQuery({
    queryId: `subscriptions-${workspaceId}`,
    query: refreshSubscriptionsQuery,
    schema: SCHEMAS.ASSESSMENT_DOC_TEMPLATE_SUBSCRIPTION_ARRAY,
  });

  useEffect(() => {
    refreshSubscriptions();
  }, [refreshSubscriptions]);

  const filteredAssessmentHistorySummaries = useMemo(() => {
    const subscriptionTemplates = subscriptions?.map(transformTemplate) ?? [];

    return assessmentHistorySummaries.filter(
      (s) =>
        s.assessment_type === 'rubric' ||
        assessmentTemplates.some((t) => t.key === s.assessment_key) ||
        subscriptionTemplates.some(
          (t) => (t.key ?? t.assessment.key) === s.assessment_key
        )
    );
  }, [assessmentHistorySummaries, subscriptions]);

  const templates = useMemo(() => {
    return subscriptionsHaveLoaded
      ? assessmentTemplates.concat(subscriptions?.map(transformTemplate) ?? [])
      : null;
  }, [subscriptionsHaveLoaded, subscriptions]);

  useEffect(() => {
    (async () => {
      try {
        await Promise.all([
          loadOrg(orgId),
          listAssessmentHistorySummaries(orgId),
        ]);
      } catch (e) {
        console.error('failed to load the org:', e);
        setError(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [loadOrg, orgId, setLoading, setError, listAssessmentHistorySummaries]);

  return (
    <OrgComponentRouter
      organization={org}
      assessmentHistorySummaries={filteredAssessmentHistorySummaries}
      workspace={workspace}
      match={match}
      loading={loading}
      error={error}
      templates={templates}
    />
  );
};

export default connect(
  (
    store,
    {
      match: {
        params: { orgId, workspaceId },
      },
    }
  ) => {
    const { entities } = store;

    return {
      org: denormalize(
        entities.organizations[orgId],
        SCHEMAS.ORGANIZATION,
        entities
      ),
      workspace: entities.workspaces[workspaceId],
      assessmentHistorySummaries:
        selectAssessmentHistorySummaries(store, orgId)?.data ?? [],
    };
  },
  {
    loadOrg,
    listAssessmentHistorySummaries,
    listSubscriptions,
  }
)(OrgContainer);
